import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Input,
  Select,
  Form,
  Tag,
  Typography,
  Spin,
  Divider,
  message,
  Space,
  Progress,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  LogoutOutlined,
  LockOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS, ROLE_STYLES, STATUS_COLORS } from '@/constants';
import { useAuthStore } from '@/stores/authStore';
import { formatShortDate } from '@/utils/date';
import type { ApiResponse, User, Subject } from '@/types';

const { Title, Text } = Typography;

interface ProfileFormValues {
  name: string;
  email: string;
  phone?: string;
  subjectIds: string[];
  gradeIds: number[];
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clearAuth } = useAuthStore();

  const [isEditing, setIsEditing] = useState(false);
  const [profileForm] = Form.useForm<ProfileFormValues>();
  const [passwordForm] = Form.useForm<PasswordFormValues>();

  const newPasswordValue: string = Form.useWatch('newPassword', passwordForm) ?? '';

  const passwordChecks = useMemo(() => ({
    length: newPasswordValue.length >= 8,
    upper: /[A-Z]/.test(newPasswordValue),
    lower: /[a-z]/.test(newPasswordValue),
    number: /[0-9]/.test(newPasswordValue),
    special: /[^A-Za-z0-9]/.test(newPasswordValue),
  }), [newPasswordValue]);

  const passwordStrengthScore = Object.values(passwordChecks).filter(Boolean).length;
  const passwordStrengthPercent = (passwordStrengthScore / 5) * 100;
  const passwordStrengthColor =
    passwordStrengthScore <= 2 ? '#ef4444' : passwordStrengthScore <= 3 ? '#f59e0b' : '#22c55e';
  const passwordStrengthLabel =
    passwordStrengthScore <= 2 ? 'Weak' : passwordStrengthScore <= 3 ? 'Fair' : passwordStrengthScore === 4 ? 'Good' : 'Strong';

  const { data: userResponse, isLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => api.get<never, ApiResponse<User>>(API_ENDPOINTS.USERS_ME),
    staleTime: 5 * 60 * 1000,
  });

  const { data: boardsResponse } = useQuery({
    queryKey: ['curriculum', 'boards'],
    queryFn: () =>
      api.get<never, ApiResponse<{ id: string; name: string; subjects: Subject[] }[]>>(API_ENDPOINTS.CURRICULUM_BOARDS),
    staleTime: 5 * 60 * 1000,
  });

  const user = userResponse?.data;
  const boards = boardsResponse?.data ?? [];

  // Flat deduplicated subject list for display (view mode)
  const subjects = useMemo(() => {
    const seen = new Set<string>();
    return boards.flatMap((b) => b.subjects).filter((s) => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
  }, [boards]);

  // Grouped options for the Select (edit mode) — grouped by board
  const subjectOptions = useMemo(() =>
    boards.map((b) => ({
      label: b.name,
      options: b.subjects.map((s) => ({ value: s.id, label: s.name })),
    })),
  [boards]);

  const updateProfileMutation = useMutation({
    mutationFn: (data: { name: string; email: string; phone?: string; subjectIds: string[]; gradeIds: number[] }) =>
      api.patch<never, ApiResponse<User>>(API_ENDPOINTS.USERS_ME, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
      message.success('Profile updated');
      setIsEditing(false);
    },
    onError: () => {
      message.error('Failed to update profile');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      // Use fetch (not the axios `api` instance) so the global 401 interceptor —
      // which redirects to /login on any 401 — does not fire for a wrong-password error.
      const token = useAuthStore.getState().token;
      const base = import.meta.env.VITE_API_BASE_URL
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
        : '/api/v1';
      const res = await fetch(`${base}${API_ENDPOINTS.AUTH_CHANGE_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw body;
      }
      return res.json() as Promise<ApiResponse<void>>;
    },
    onSuccess: () => {
      message.success('Password changed. Please log in again.');
      clearAuth();
      navigate('/login');
    },
    onError: () => {
      message.error('Failed to change password. Check your current password.');
    },
  });

  const handleStartEditing = () => {
    if (user) {
      profileForm.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone ?? '',
        subjectIds: user.subjectIds ?? [],
        gradeIds: user.gradeIds ?? [],
      });
    }
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    profileForm.resetFields();
  };

  const handleSaveProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      updateProfileMutation.mutate({
        ...values,
        phone: values.phone?.trim() || undefined,
      });
    } catch {
      // Validation errors shown by form
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error('New passwords do not match');
        return;
      }
      changePasswordMutation.mutate({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
    } catch {
      // Validation errors shown by form
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) return null;

  const roleStyle = ROLE_STYLES[user.role] ?? ROLE_STYLES.TEACHER;
  const statusColor = STATUS_COLORS[user.status] ?? '#94a3b8';

  return (
    <div>
      <Title level={3} style={{ marginBottom: 20 }}>
        Profile
      </Title>

      <Card style={{ borderRadius: 12, marginBottom: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                fontWeight: 700,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {user.name}
              </Title>
              <Space size="small" style={{ marginTop: 4 }}>
                <Tag
                  style={{
                    color: roleStyle.color,
                    background: roleStyle.bg,
                    border: 'none',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {user.role}
                </Tag>
                <Tag
                  style={{
                    background: statusColor + '20',
                    color: statusColor,
                    border: 'none',
                    fontWeight: 600,
                  }}
                >
                  {user.status}
                </Tag>
              </Space>
            </div>
          </div>
          {!isEditing ? (
            <Button icon={<EditOutlined />} onClick={handleStartEditing}>
              Edit
            </Button>
          ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={handleCancelEditing}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={updateProfileMutation.isPending}
                onClick={handleSaveProfile}
              >
                Save
              </Button>
            </Space>
          )}
        </div>

        {isEditing ? (
          <Form form={profileForm} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: 'Name is required' },
                { max: 100, message: 'Max 100 characters' },
              ]}
            >
              <Input prefix={<UserOutlined />} />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input prefix={<MailOutlined />} readOnly style={{ cursor: 'default', background: '#f8fafc', color: '#94a3b8' }} />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                {
                  pattern: /^\+?[0-9\s\-()]{7,20}$/,
                  message: 'Enter a valid phone number',
                },
              ]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="e.g. +91 98765 43210" />
            </Form.Item>
            <Form.Item name="gradeIds" label="Grades You Teach">
              <Select
                mode="multiple"
                placeholder="Select grades"
                options={[
                  { value: 0, label: 'Kindergarten' },
                  ...Array.from({ length: 12 }, (_, i) => ({
                    value: i + 1,
                    label: `Grade ${i + 1}`,
                  })),
                ]}
              />
            </Form.Item>
            <Form.Item name="subjectIds" label="Subjects You Teach">
              <Select
                mode="multiple"
                placeholder="Select subjects"
                optionFilterProp="label"
                options={subjectOptions}
              />
            </Form.Item>
          </Form>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <ProfileRow
              icon={<MailOutlined />}
              label="Email"
              value={user.email}
            />
            {user.phone && (
              <ProfileRow
                icon={<PhoneOutlined />}
                label="Phone"
                value={user.phone}
              />
            )}
            <ProfileRow
              icon={<CalendarOutlined />}
              label="Member Since"
              value={formatShortDate(user.createdAt)}
            />
            {user.gradeIds && user.gradeIds.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text type="secondary" style={{ minWidth: 100 }}>
                  Grades
                </Text>
                <Space size={[0, 4]} wrap>
                  {user.gradeIds.map((g) => (
                    <Tag key={g}>{g === 0 ? 'Kindergarten' : `Grade ${g}`}</Tag>
                  ))}
                </Space>
              </div>
            )}
            {user.subjectIds && user.subjectIds.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Text type="secondary" style={{ minWidth: 100 }}>
                  Subjects
                </Text>
                <Space size={[0, 4]} wrap>
                  {user.subjectIds.map((sid) => {
                    const subj = subjects.find((s) => s.id === sid);
                    return <Tag key={sid}>{subj?.name ?? sid}</Tag>;
                  })}
                </Space>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card
        style={{ borderRadius: 12, marginBottom: 24 }}
        title={
          <Space>
            <LockOutlined style={{ color: '#6366f1' }} />
            <span>Change Password</span>
          </Space>
        }
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Enter your current password' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter current password" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Enter a new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
          </Form.Item>

          {newPasswordValue.length > 0 && (
            <div style={{ marginBottom: 20, marginTop: -8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Progress
                  percent={passwordStrengthPercent}
                  showInfo={false}
                  strokeColor={passwordStrengthColor}
                  trailColor="#e2e8f0"
                  size="small"
                  style={{ flex: 1, margin: 0 }}
                />
                <Text style={{ fontSize: 12, color: passwordStrengthColor, fontWeight: 600, minWidth: 48 }}>
                  {passwordStrengthLabel}
                </Text>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
                {[
                  { key: 'length', label: 'At least 8 characters' },
                  { key: 'upper', label: 'Uppercase letter' },
                  { key: 'lower', label: 'Lowercase letter' },
                  { key: 'number', label: 'Number' },
                  { key: 'special', label: 'Special character' },
                ].map(({ key, label }) => {
                  const passed = passwordChecks[key as keyof typeof passwordChecks];
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {passed
                        ? <CheckCircleFilled style={{ color: '#22c55e', fontSize: 12 }} />
                        : <CloseCircleFilled style={{ color: '#cbd5e1', fontSize: 12 }} />}
                      <Text style={{ fontSize: 12, color: passed ? '#64748b' : '#94a3b8' }}>{label}</Text>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            rules={[
              { required: true, message: 'Confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Re-enter new password" />
          </Form.Item>

          <Button
            type="primary"
            icon={<LockOutlined />}
            loading={changePasswordMutation.isPending}
            onClick={handleChangePassword}
          >
            Update Password
          </Button>
        </Form>
      </Card>

      <Divider />

      <Button
        danger
        icon={<LogoutOutlined />}
        size="large"
        block
        onClick={handleLogout}
      >
        Logout
      </Button>
    </div>
  );
}

function ProfileRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ color: '#94a3b8', fontSize: 16 }}>{icon}</span>
      <Text type="secondary" style={{ minWidth: 100 }}>
        {label}
      </Text>
      <Text>{value}</Text>
    </div>
  );
}
